/**
 * FairwayBid – Vergabevermerk
 *
 * Viele Golfanlagen sind Vereine oder kommunal getragen. Dort muss jemand dem
 * Vorstand, dem Kämmerer oder dem Prüfer erklären, warum Anbieter B den
 * Zuschlag bekommen hat und nicht der billigste. Wer das aus E-Mails
 * zusammensuchen muss, entscheidet im Zweifel gar nicht.
 *
 * Dieses Modul baut daraus ein Dokument: alle Angebote nebeneinander, die
 * Kriterien, die Entscheidung, das Datum. Ausgegeben wird druckfertiges HTML –
 * der Club druckt es oder sichert es als PDF. Bewusst ohne zusätzliche
 * Bibliothek: Ein Vermerk, der von einem Paket abhängt, das jemand pflegen
 * muss, ist in zwei Jahren kaputt.
 *
 * Reine Zeichenkettenarbeit, kein React – damit prüfbar.
 */

import { escapeHtml, formatDate, providerName, BASE_STYLES, PRINT_BUTTON } from './html';
import { OFFER_FIELDS, getOfferFieldValue, getOfferFieldRaw } from './offerFields';
import { getOfferAmount, formatAmount, getOfferStatus, STATUS_LABELS } from './offers';
import { getDeadlineStatus } from './deadlines';
import { getCategoryLabel } from './matching';
import { describeCoverage } from './coverage';

/**
 * Zeilen des Vermerks: erst der Preis, dann die Kriterien in fester
 * Reihenfolge – dieselbe wie im Angebotsvergleich, damit beides zusammenpasst.
 */
export const buildAwardRows = (offers = [], listing = null) => {
  const list = Array.isArray(offers) ? offers : [];
  const country = listing?.attributes?.publicData?.delivery_country || null;
  return [
    {
      label: 'Gesamtpreis netto',
      values: list.map(tx => formatAmount(getOfferAmount(tx)) || 'auf Anfrage'),
    },
    ...OFFER_FIELDS.map(field => ({
      label: field.label,
      values: list.map(tx => getOfferFieldValue(field.key, getOfferFieldRaw(tx, field.key)) || '–'),
    })),
    {
      label: 'Liefergebiet und Service',
      values: list.map(tx => describeCoverage(tx?.provider, country)),
    },
    {
      label: 'Status',
      values: list.map(tx => STATUS_LABELS[getOfferStatus(tx)] || getOfferStatus(tx)),
    },
  ];
};

/**
 * Welches Angebot hat den Zuschlag? Angenommen zählt, sonst offen.
 */
export const getAwardedOffer = (offers = []) =>
  (Array.isArray(offers) ? offers : []).find(tx => getOfferStatus(tx) === 'angenommen') || null;

/**
 * Druckfertiges HTML des Vermerks.
 *
 * @param {Object} params
 * @param {Object} params.listing Die Anfrage
 * @param {Array} params.offers Die Angebote (Transaktionen)
 * @param {string} params.clubName Name der ausschreibenden Anlage
 * @param {Date} params.now Erstellungszeitpunkt
 * @returns {string} vollständiges HTML-Dokument
 */
export const buildAwardHtml = ({ listing, offers = [], clubName = '', now = new Date() } = {}) => {
  const list = Array.isArray(offers) ? offers : [];
  const rows = buildAwardRows(list, listing);
  const awarded = getAwardedOffer(list);
  const deadline = getDeadlineStatus(listing, now);
  const title = listing?.attributes?.title || 'Bedarfsanfrage';

  const head = list.map(tx => `<th>${escapeHtml(providerName(tx))}</th>`).join('');
  const body = rows
    .map(
      row =>
        `<tr><th class="crit">${escapeHtml(row.label)}</th>${row.values
          .map(v => `<td>${escapeHtml(v)}</td>`)
          .join('')}</tr>`
    )
    .join('');

  const decision = awarded
    ? `Der Zuschlag wurde <strong>${escapeHtml(providerName(awarded))}</strong> erteilt` +
      `${
        formatAmount(getOfferAmount(awarded))
          ? ` zum Gesamtpreis von ${escapeHtml(formatAmount(getOfferAmount(awarded)))} netto`
          : ''
      }.`
    : 'Zum Zeitpunkt dieses Vermerks ist noch keine Entscheidung getroffen.';

  const offersBlock =
    list.length === 0
      ? '<p>Zu dieser Anfrage liegt noch kein Angebot vor.</p>'
      : `<table><thead><tr><th class="crit">Kriterium</th>${head}</tr></thead><tbody>${body}</tbody></table>`;

  return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>Vergabevermerk – ${escapeHtml(title)}</title>
<style>${BASE_STYLES}</style>
</head>
<body>
  <h1>Vergabevermerk</h1>
  <p class="sub">${escapeHtml(title)}</p>

  <h2>Ausschreibung</h2>
  <dl>
    <dt>Ausschreibende Stelle</dt><dd>${escapeHtml(clubName || '–')}</dd>
    <dt>Kategorie</dt><dd>${escapeHtml(getCategoryLabel(listing))}</dd>
    <dt>Veröffentlicht am</dt><dd>${escapeHtml(
      formatDate(listing?.attributes?.publishedAt || listing?.attributes?.createdAt)
    )}</dd>
    <dt>Angebotsfrist</dt><dd>${escapeHtml(deadline ? deadline.dateLabel : '–')}</dd>
    <dt>Eingegangene Angebote</dt><dd>${list.length}</dd>
  </dl>

  <h2>Angebotsvergleich</h2>
  ${offersBlock}

  <h2>Entscheidung</h2>
  <p class="decision">${decision}</p>

  <footer>
    Erstellt am ${escapeHtml(formatDate(now))} über FairwayBid. Die Angaben stammen aus den
    Angeboten der Anbieter; für ihre Richtigkeit sind die Anbieter verantwortlich.
  </footer>

  ${PRINT_BUTTON}
</body>
</html>`;
};
